import _ from "lodash";
import { BarChart3, Search, SearchX, X } from "lucide-react-native";
import { FC, useCallback, useEffect, useRef, useState } from "react";
import { FlatList, View } from "react-native";

import {
  Box,
  Card,
  Center,
  ErrorWithRetry,
  HStack,
  Input,
  InputField,
  InputIcon,
  InputSlot,
  Skeleton,
  Text,
  VStack,
} from "@/components/ui";
import { useLocalization } from "@/hooks";
import API from "@/hooks/api";
import { AdhocQuestion } from "@/types/resources";
import { QuestionType } from "@/types/utils";
import QuestionCard from "./QuestionCard";

interface QuestionsProps {
  type: QuestionType;
}

const Questions: FC<QuestionsProps> = ({ type }) => {
  const { i18n } = useLocalization();
  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearchText, setDebouncedSearchText] = useState("");

  const itemsPerPage = 20;

  const [debouncedSearch] = useState(() =>
    _.debounce((text: string) => {
      setDebouncedSearchText(text);
    }, 500)
  );

  useEffect(() => {
    debouncedSearch(searchInput);
  }, [searchInput, debouncedSearch]);

  // Reset to the first page during render (not in an effect) when the debounced
  // search text changes.
  const [prevDebouncedSearchText, setPrevDebouncedSearchText] =
    useState(debouncedSearchText);
  if (prevDebouncedSearchText !== debouncedSearchText) {
    setPrevDebouncedSearchText(debouncedSearchText);
    setPage(1);
  }

  const handleClearSearch = useCallback(() => {
    debouncedSearch.cancel();
    setSearchInput("");
    setDebouncedSearchText("");
  }, [debouncedSearch]);

  const { count, error, loading, questions, refresh, setCount, setQuestions } =
    API.Adhoc.useQuestions(page, itemsPerPage, type, debouncedSearchText);

  const getMoreRef = useRef(
    _.debounce(() => {
      setPage((prev) => prev + 1);
    }, 500)
  ).current;

  const getMore = useCallback(() => {
    if (questions.length < count && !loading) {
      getMoreRef();
    }
  }, [count, questions.length, loading, getMoreRef]);

  const renderItem = ({ item }: { item: AdhocQuestion }) => {
    return (
      <Box className="flex-1 py-1.5 px-1" style={{ maxWidth: "50%" }}>
        <QuestionCard
          onDeleted={() => {
            setQuestions((prev) => prev.filter((q) => q.id !== item.id));
            setCount((prev) => prev - 1);
          }}
          question={item}
        />
      </Box>
    );
  };

  const renderSkeletonCard = (key: number) => (
    <Card
      className="flex-1 border border-outline-200 rounded-2xl px-5 py-4"
      key={key}
    >
      <VStack className="flex-1 gap-2">
        <HStack className="items-center gap-2">
          <Skeleton className="h-4 flex-1 rounded" />
          <Skeleton className="h-5 w-14 rounded-full" />
        </HStack>
        <Skeleton className="h-3 w-1/2 rounded" />
        <HStack className="items-center justify-between mt-1">
          <HStack className="items-center gap-3">
            <Skeleton className="h-3 w-10 rounded" />
            <Skeleton className="h-3 w-16 rounded" />
          </HStack>
          <HStack className="items-center gap-1.5">
            <Skeleton className="w-4 h-4 rounded-full" />
            <Skeleton className="h-3 w-14 rounded" />
          </HStack>
        </HStack>
      </VStack>
    </Card>
  );

  const renderFooter = () =>
    loading ? (
      <VStack className="gap-3">
        {[0, 2, 4].map((i) => (
          <HStack className="gap-3" key={i}>
            {renderSkeletonCard(i)}
            {renderSkeletonCard(i + 1)}
          </HStack>
        ))}
      </VStack>
    ) : null;

  return (
    <Box className="flex-1 pt-4 pb-6">
      <HStack className="items-end justify-between mb-5">
        <Text className="text-lg font-semibold text-typography-900">
          {i18n.t("dashboard.pastQuestions")}
        </Text>
        <Input className="w-full max-w-xs border border-outline-200 rounded-full bg-background-50">
          <InputSlot className="pl-3">
            <InputIcon as={Search} className="text-typography-500" />
          </InputSlot>
          <InputField
            onChangeText={setSearchInput}
            placeholder={i18n.t("misc.searchQuestions")}
            value={searchInput}
          />
          {searchInput.length > 0 && (
            <InputSlot className="pr-3" onPress={handleClearSearch}>
              <InputIcon as={X} className="text-typography-500" />
            </InputSlot>
          )}
        </Input>
      </HStack>

      <FlatList
        columnWrapperStyle={{ gap: 8 }}
        contentContainerStyle={{ overflow: "visible" }}
        numColumns={2}
        ListEmptyComponent={
          loading ? undefined : (
            <View className="flex-1">
              {error ? (
                <ErrorWithRetry
                  error={error}
                  onRetry={refresh}
                  title={i18n.t("misc.errorLoadingQuestions")}
                />
              ) : debouncedSearchText ? (
                <Center className="py-12">
                  <VStack className="items-center gap-3">
                    <SearchX className="text-typography-400" size={48} />
                    <Text className="text-lg font-medium text-typography-700">
                      {i18n.t("misc.noSearchResults")}
                    </Text>
                    <Text className="text-sm text-typography-500">
                      {i18n.t("misc.noSearchResultsDescription")}
                    </Text>
                  </VStack>
                </Center>
              ) : (
                <Center className="py-12">
                  <VStack className="items-center gap-4">
                    <BarChart3 className="text-typography-300" size={48} />
                    <Text className="text-lg font-medium text-typography-700">
                      {i18n.t("misc.noQuestionsYet")}
                    </Text>
                  </VStack>
                </Center>
              )}
            </View>
          )
        }
        ListFooterComponent={renderFooter}
        className="flex-1"
        contentContainerClassName="flex-grow"
        data={questions}
        keyExtractor={(item) => item.id}
        onEndReached={getMore}
        onEndReachedThreshold={0.5}
        renderItem={renderItem}
      />
    </Box>
  );
};

export default Questions;
